<template>
  <div class="rule-edit-history-view flex flex-col gap-4 py-5">
    <empty-screen
      v-if="isListEmpty && !isLoading"
      icon="refresh-v2"
      :title="tr('No changes recorded for this automation')"
      :flag-text="tr('Original setup active')"
      :description="tr('This automation is running with its original settings. When you or team members modify any settings, the complete change history will appear here.')"
    />

    <section
      v-for="(item, key) in logs"
      :key="'item-' + key"
      class="glass-panel flex flex-col gap-4 rounded-xl p-5"
    >
      <header class="flex items-center gap-3">
        <ui-avatar class="size-8">
          <ui-avatar-fallback class="text-xs font-semibold">
            <pencil-icon v-if="item.changes.length !== 0 || key !== lastItemKey" class="size-3.5" />
            <plus-icon v-else class="size-3.5" />
          </ui-avatar-fallback>
        </ui-avatar>
        <div class="flex flex-col">
          <h2 class="m-0 text-sm font-semibold text-foreground">{{ buildTitle(item, key) }}</h2>
          <span v-tooltip="formatToYMD(item.created_at) + ', ' + formatTime(item.created_at)" class="w-fit text-xs text-muted-foreground">
            {{ timeAgo(item.created_at, tr) }}
          </span>
        </div>
      </header>

      <rule-change-log-fields v-if="item.changes.length !== 0 || key !== lastItemKey" :entity="item" />
    </section>

    <template v-if="isLoading || hasMore">
      <div
        v-for="i in (isListEmpty ? 3 : 1)"
        :key="'edit-history-skeleton-' + i"
        ref="scrollPagerTrigger"
        class="glass-panel flex items-center gap-3 rounded-xl p-5"
      >
        <ui-skeleton class="size-8 rounded-full" />
        <div class="flex flex-col gap-1.5">
          <ui-skeleton class="h-4 w-56" />
          <ui-skeleton class="h-3 w-20" />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { Pencil, Plus } from '@lucide/vue';

import ScrollPagerMixin from '@/mixins/ScrollPager.js';
import RuleService from '@/services/Rule.js';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
import RuleChangeLogFields from '@/ui/components/RuleChangeLogFields.vue';
import { formatTime, formatToYMD, timeAgo } from '@/utils/date.js';

const ITEM_PER_PAGE = 20;

export default {
  name: 'RuleEditHistoryView',
  components: {
    EmptyScreen,
    PencilIcon: Pencil,
    PlusIcon: Plus,
    RuleChangeLogFields,
  },
  mixins: [ ScrollPagerMixin ],
  data:() => ({
    isLoading: false,
    hasMore: true,
    page: 0,
    logs: [],
  }),
  computed: {
    ruleId() {
      return this.$route.params.id;
    },
    isListEmpty() {
      return this.logs.length === 0;
    },
    lastItemKey() {
      return this.logs.length - 1;
    },
  },
  created() {
    this.loadNewData();
  },
  methods: {
    timeAgo,
    formatToYMD,
    formatTime,
    loadNewData() {
      if (this.isLoading || !this.hasMore) {
        return;
      }

      this.isLoading = true;
      ++this.page;

      const params = {
        id: this.ruleId,
        page: this.page,
        limit: ITEM_PER_PAGE,
      };

      RuleService.getChangeLogs(params)
        .then(resp => {
          resp.forEach(item => this.logs.push(item));
          this.hasMore = resp.length === ITEM_PER_PAGE;

          return resp;
        })
        .catch(err => {
          this.hasMore = false;
          this.error(err);
        })
        .finally(() => {
          this.isLoading = false;
          if (this.hasMore) {
            this.$nextTick(() => this.initScrollPager('scrollPagerTrigger', 'loadNewData'));
          }
        });
    },
    buildTitle(item, key) {
      if (key === this.lastItemKey && item.changes.length === 0) {
        return this.tr(':name created the automation', { ':name': item.user.fullname });
      }

      return this.tr(':name modified the automation', { ':name': item.user.fullname });
    },
  },
};
</script>
